import { i18n } from "@tellescope/i18n";
import {
  Badge,
  BadgeProps,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@tellescope/ui";
import { useCallback } from "react";

export interface BadgeConfig {
  label: string;
  bgColor: string;
  textColor: string;
}

export interface BadgeDropdownCellProps<T = unknown> {
  value: T;
  options: readonly T[];
  getConfig?: (value: T) => BadgeConfig;
  onChange: (newValue: T) => void | Promise<void>;
  disabled?: boolean;
  unknownLabel?: string;
  unselectedBadgeVariant?: BadgeProps["variant"];
  selectedBadgeVariant?: BadgeProps["variant"];
  valueBadgeVariant?: BadgeProps["variant"];
  title?: string;
  size?: "default" | "large";
}

export function BadgeDropdownCell<T = unknown>({
  value,
  options,
  getConfig,
  onChange,
  disabled = false,
  unknownLabel = i18n.t("Unknown", { ns: "common" }),
  selectedBadgeVariant = "default",
  unselectedBadgeVariant = "outline",
  valueBadgeVariant = "outline",
  title,
  size = "default",
}: BadgeDropdownCellProps<T>) {
  const config = getConfig?.(value);

  const handleChange = useCallback(
    (newValue: T) => {
      onChange(newValue);
    },
    [onChange]
  );

  if (disabled) {
    return (
      <Badge className={cn(config ? [config.bgColor, config.textColor] : "")}>{config?.label ?? unknownLabel}</Badge>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <button onClick={(e) => e.stopPropagation()}>
          <Badge variant={valueBadgeVariant} className={cn(config ? [config.bgColor, config.textColor] : "")}>
            {(config?.label ?? unknownLabel) || String(value)}
          </Badge>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={10}>
        {!!title && <p className="font-bold text-xs px-2 pt-2 pb-1">{title}</p>}
        {options.map((option) => {
          const optionConfig = getConfig?.(option);
          return (
            <DropdownMenuItem
              key={String(option)}
              onClick={() => handleChange(option)}
              className={cn("cursor-pointer", size === "large" && "w-full min-w-[175px]")}
            >
              <Badge
                variant={option === value ? selectedBadgeVariant : unselectedBadgeVariant}
                className={cn(
                  optionConfig?.bgColor,
                  optionConfig?.textColor,
                  "pointer-events-none",
                  size === "large" && "w-full justify-start px-2 py-1.5"
                )}
              >
                {optionConfig?.label ?? String(option)}
              </Badge>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
